import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import axiosInstance from '../../utils/axios';
import { fetchProfile } from '../../store/features/profileSlice';
import userAvatar from '../../assets/Images/user.png';

const ProfilePictureModal = (props) => {
  const { show, onHide } = props;
  const dispatch = useDispatch();
  const { id: paramId } = useParams();

  const { profile } = useSelector((state) => state.profile);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);

  const [imageLoadError, setImageLoadError] = useState(false);

  useEffect(() => {
    if (show) {
      setImageLoadError(false);
      setPreviewUrl(null);
      setSelectedFile(null);
    }
  }, [show, profile]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 3 * 1024 * 1024) {
      setError('Image size must be less than 3MB');
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    setError(null);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setImageLoadError(false);
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    const targetId = profile?.field_officer_id || paramId || profile?.user_id;

    if (!targetId) {
      console.error('Profile data:', profile);
      console.error('Param ID:', paramId);
      setError('Unable to identify user. Please refresh the page and try again.');
      return;
    }

    const formData = new FormData();
    formData.append('profilePicture', selectedFile);
    formData.append('field_officer_id', targetId);

    try {
      setError(null); // Clear previous errors
      console.log('Uploading profile picture for field_officer_id:', targetId);
      const { data } = await axiosInstance.post('/upload-img', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (data.success) {
        onHide();
        dispatch(fetchProfile());
        setSelectedFile(null);
        setPreviewUrl(null);
      } else {
        setError(data.msg || 'Upload failed. Please try again.');
      }
    } catch (err) {
      console.error('Upload error:', err);
      console.error('Error response:', err.response?.data);
      const errorMessage = err.response?.data?.msg || err.message || 'Upload failed. Please try again.';
      setError(errorMessage);
    }
  };

  const currentImage = previewUrl ||
    (!imageLoadError && profile?.profile_picture && profile.profile_picture !== 'Default.jpg'
      ? profile.profile_picture
      : userAvatar);

  return (
    <Modal
      {...props}
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          Update Profile Picture
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column align-items-center gap-4 py-2">
          {/* Circular Image Preview */}
          <div
            className="border rounded-circle overflow-hidden d-flex shadow justify-content-center align-items-center"
            style={{ width: '200px', height: '200px', backgroundColor: '#f0f0f0' }}
          >
            <img
              src={currentImage}
              alt="Profile Preview"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                e.target.onerror = null;
                setImageLoadError(true);
              }}
            />
          </div>

          {/* File Input */}
          <div className="w-100 text-center">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="form-control"
            />
          </div>

          {error && <p className="text-danger m-0">{error}</p>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="dark" onClick={handleSubmit}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProfilePictureModal;